import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Autocomplete } from '@material-ui/lab';
import { Button, CircularProgress, TextField } from '@material-ui/core';
import { Add, Close } from '@material-ui/icons';
import { toast } from 'react-toastify';

import {
  listCompanies,
  createCompany,
  updateCompany,
} from '../../../../../services/certificateBatches';
import { useDebounce } from '../../../../../hooks/useDebounce';
import {
  Wrapper,
  SectionTitle,
  FieldRow,
  CreatePanel,
  CreatePanelRow,
  FileHint,
  FileError,
  PreviewRow,
  CompanyLogoPreview,
  ColorLabel,
  ColorSwatchRow,
  ColorSwatch,
  ColorInput,
  ColorCode,
} from './styles';

const MAX_LOGO_SIZE = 2 * 1024 * 1024; // 2MB
const ACCEPTED_TYPES = ['image/png', 'image/jpeg'];

const validateFile = (file) => {
  if (!file) return '';
  if (!ACCEPTED_TYPES.includes(file.type)) {
    return 'Formato inválido. Envie um arquivo PNG ou JPG.';
  }
  if (file.size > MAX_LOGO_SIZE) {
    return 'Arquivo muito grande. O limite é 2MB.';
  }
  return '';
};

// value: objeto empresa completo ou null
export default function CompanyForm({ value, onChange }) {
  const [companies, setCompanies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);

  const [isCreating, setIsCreating] = useState(false);
  const [name, setName] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [color, setColor] = useState('#000000');
  const [suggestedColors, setSuggestedColors] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdatingColor, setIsUpdatingColor] = useState(false);

  const selectedIdRef = useRef(null);
  const reqSeq = useRef(0);

  const loadCompanies = useCallback(async (searchTerm) => {
    const seq = ++reqSeq.current;
    setIsLoadingCompanies(true);
    try {
      const { data } = await listCompanies(searchTerm);
      if (seq !== reqSeq.current) return;
      setCompanies(Array.isArray(data) ? data : []);
    } catch (error) {
      if (seq !== reqSeq.current) return;
      toast.error('Não foi possível carregar as empresas.');
    } finally {
      if (seq === reqSeq.current) setIsLoadingCompanies(false);
    }
  }, []);

  useEffect(() => {
    loadCompanies(debouncedSearch);
  }, [debouncedSearch, loadCompanies]);

  useEffect(() => {
    if (value && value.id !== selectedIdRef.current) {
      setSelected(value);
      setSearch(value.name);
      selectedIdRef.current = value.id;
    }
  }, [value]);

  const select = (company, { keepSuggestions = false } = {}) => {
    setSelected(company);
    selectedIdRef.current = company ? company.id : null;
    if (!keepSuggestions) {
      setSuggestedColors([]);
    }
    setColor((company && company.color) || '#000000');
    if (onChange) onChange(company);
  };

  const handleFileChange = (event) => {
    const file = (event.target.files && event.target.files[0]) || null;
    const error = validateFile(file);
    if (error) {
      setFileError(error);
      setLogoFile(null);
      event.target.value = '';
      return;
    }
    setFileError('');
    setLogoFile(file);
  };

  const handleCreate = async () => {
    if (!name || !logoFile) return;
    setIsSaving(true);
    try {
      const { data } = await createCompany({ name, color, logoFile });
      setSuggestedColors(data.suggestedColors || []);
      setCompanies((prev) => [...prev, data.company]);
      setIsCreating(false);
      setName('');
      setLogoFile(null);
      select(data.company, { keepSuggestions: true });
      setSearch(data.company.name);
      toast.success('Empresa criada com sucesso.');
    } catch (error) {
      toast.error(
        'Não foi possível criar a empresa. Verifique o nome e o logo (PNG/JPG até 2MB).'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const applyColor = async (hex) => {
    setColor(hex);
    if (!selected) return;
    const targetId = selected.id;
    setIsUpdatingColor(true);
    try {
      const { data } = await updateCompany(targetId, { name: selected.name, color: hex });
      setCompanies((prev) => prev.map((c) => (c.id === data.company.id ? data.company : c)));
      if (data.company.id === targetId && selectedIdRef.current === targetId) {
        select(data.company);
      }
    } catch (error) {
      if (selectedIdRef.current === targetId) {
        setColor((selected && selected.color) || '#000000');
      }
      toast.error('Não foi possível atualizar a cor da empresa.');
    } finally {
      if (selectedIdRef.current === targetId) {
        setIsUpdatingColor(false);
      }
    }
  };

  return (
    <Wrapper>
      <SectionTitle>Empresa</SectionTitle>
      <FieldRow>
        <Autocomplete
          options={companies}
          loading={isLoadingCompanies}
          value={selected}
          inputValue={search}
          noOptionsText="Nenhuma empresa encontrada"
          getOptionSelected={(option, val) => option?.id === val?.id}
          getOptionLabel={(company) => (company && company.name) || ''}
          onInputChange={(event, newValue, reason) => {
            if (reason === 'input') {
              setSearch(newValue);
            } else if (reason === 'clear') {
              setSearch('');
              select(null);
            } else if (reason === 'reset') {
              setSearch(selected ? selected.name : '');
            }
          }}
          onChange={(event, newValue) => {
            select(newValue);
            setSearch(newValue ? newValue.name : '');
          }}
          style={{ minWidth: 260 }}
          renderInput={(params) => (
            <TextField {...params} label="Buscar empresa" placeholder="Digite o nome…" />
          )}
        />
        <Button
          type="button"
          onClick={() => setIsCreating((v) => !v)}
          startIcon={isCreating ? <Close /> : <Add />}
        >
          {isCreating ? 'Cancelar' : 'Nova empresa'}
        </Button>
      </FieldRow>

      {isCreating && (
        <CreatePanel>
          <CreatePanelRow>
            <TextField
              label="Nome da empresa"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
            />
          </CreatePanelRow>
          <CreatePanelRow>
            <input type="file" accept="image/png,image/jpeg" onChange={handleFileChange} />
            <FileHint>PNG ou JPG, até 2MB</FileHint>
            {fileError && <FileError>{fileError}</FileError>}
          </CreatePanelRow>
          <Button
            type="button"
            variant="contained"
            color="primary"
            disabled={!name || !logoFile || !!fileError || isSaving}
            onClick={handleCreate}
          >
            {isSaving ? <CircularProgress size={20} /> : 'Salvar empresa'}
          </Button>
        </CreatePanel>
      )}

      {selected && (
        <PreviewRow>
          {selected.logo_url && (
            <CompanyLogoPreview src={selected.logo_url} alt={selected.name} />
          )}
          <ColorLabel>Cor:</ColorLabel>
          <ColorSwatchRow>
            {suggestedColors.map((hex) => (
              <ColorSwatch
                key={hex}
                type="button"
                title={hex}
                isActive={color === hex}
                swatchColor={hex}
                disabled={isUpdatingColor}
                onClick={() => applyColor(hex)}
              />
            ))}
          </ColorSwatchRow>
          <ColorInput
            type="color"
            value={selected.color || color}
            disabled={isUpdatingColor}
            onChange={(e) => applyColor(e.target.value)}
          />
          <ColorCode>{selected.color || color}</ColorCode>
          {isUpdatingColor && <CircularProgress size={16} />}
        </PreviewRow>
      )}
    </Wrapper>
  );
}
